// Timed product sales — mirrors backend/product_sale.py. The server stays authoritative for charged prices.
export const SALE_TZ = 'America/Chicago';

export const parseTs = (v) => {
  if (!v) return null;
  const ms = Date.parse(v);
  return Number.isFinite(ms) ? ms : null;
};

export const saleStatus = (product, now = Date.now()) => {
  if (!product?.sale_enabled) return 'off';
  const s = parseTs(product.sale_starts_at);
  const e = parseTs(product.sale_ends_at);
  if (s == null || e == null || e <= s) return 'off';
  if (now < s) return 'scheduled';
  if (now >= e) return 'ended';
  return 'active';
};

// Sale info for one sellable dosage (parent catNo or a variant catNo). Variants are matched first so a
// "variant view" of a product ({...product, catNo: variant.catNo, price1: variant.price}) resolves correctly.
export const saleForCatNo = (product, catNo, now = Date.now()) => {
  if (!product) return { active: false };
  const variant = (product.variants || []).find((v) => v.catNo && v.catNo === catNo);
  const regular = Number(variant ? variant.price : product.price1) || 0;
  const raw = variant ? variant.sale_price : (catNo === product.catNo ? product.sale_price : null);
  const salePrice = raw == null || raw === '' ? null : Number(raw);
  const active = saleStatus(product, now) === 'active' && Number.isFinite(salePrice) && salePrice > 0 && salePrice < regular;
  return active
    ? { active: true, salePrice: Math.round(salePrice * 100) / 100, regular, endsAt: parseTs(product.sale_ends_at) }
    : { active: false, regular };
};

export const anyDosageOnSale = (product, now = Date.now()) => {
  if (saleStatus(product, now) !== 'active') return false;
  return [product.catNo, ...(product.variants || []).map((v) => v.catNo)].some((c) => saleForCatNo(product, c, now).active);
};

export const nextSaleBoundary = (products, now = Date.now()) => {
  let next = null;
  for (const p of products || []) {
    const st = saleStatus(p, now);
    const t = st === 'scheduled' ? parseTs(p.sale_starts_at) : st === 'active' ? parseTs(p.sale_ends_at) : null;
    if (t != null && (next == null || t < next)) next = t;
  }
  return next;
};

const tzOffsetMs = (utcMs) => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: SALE_TZ, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(utcMs)).map((p) => [p.type, p.value]));
  return Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second) - utcMs;
};

// "2026-03-08T01:30" (Central wall clock) → UTC ISO. Returns null for invalid or non-existent (DST gap) times.
export const centralInputToUtcIso = (local) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local || '');
  if (!m) return null;
  const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  let utc = guess - tzOffsetMs(guess);
  utc = guess - tzOffsetMs(utc);
  return utcIsoToCentralInput(new Date(utc).toISOString()) === local ? new Date(utc).toISOString() : null;
};

export const utcIsoToCentralInput = (iso) => {
  const ms = parseTs(iso);
  if (ms == null) return '';
  const d = new Date(ms + tzOffsetMs(ms));
  return d.toISOString().slice(0, 16);
};

export const formatCentral = (iso) => {
  const ms = parseTs(iso);
  if (ms == null) return '';
  return `${new Intl.DateTimeFormat('en-US', { timeZone: SALE_TZ, dateStyle: 'medium', timeStyle: 'short' }).format(new Date(ms))} CT`;
};

// Form value → ISO for saving. Returns the stored instant unchanged (incl. seconds) when the Central minute
// shown in the form still matches it, so unrelated saves never shift a sale boundary.
export const preserveSaleTs = (local, origIso) => {
  if (origIso && local && utcIsoToCentralInput(origIso) === local) return origIso;
  return centralInputToUtcIso(local);
};
